import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import * as api from '../api/client'
import type { Category, DeviceAssessment, PriceQuote, SellGptConfig } from '../api/client'
import { ApiError } from '../api/client'
import { firstAnswerableStep, nextAnswerableStep, questionFor, type Question } from './guided'
import { fill, languageOfScript, phrases } from './phrases'
import {
  initialSale,
  quoteRequestFor,
  saleReducer,
  type ChosenDevice,
  type PricedSelection,
  type SaleState,
} from './sale'
import { draftStorage } from './storage'

/**
 * The sale engine: the one place the conversation, the catalogue and Cashit's valuation meet.
 *
 * TWO PATHS, ONE OUTCOME
 *   With the assistant available, what the customer types goes to /api/v1/sellgpt/chat and comes back as
 *   a reply plus the JSON each tool returned. Without it, the same typing is matched against the
 *   catalogue directly. Either way the DATA on screen - the model, the questions, the price - comes from
 *   the same Cashit endpoints, and the lead that is created is identical. The assistant changes the
 *   wording of the conversation and nothing else.
 *
 * WHY TOOL RESULTS RATHER THAN THE REPLY TEXT
 *   Everything shown in the quote panel is read out of the tool results the server returned, never parsed
 *   out of the assistant's prose. A model that writes "about twenty-four thousand" cannot change what is
 *   displayed, because the displayed figure was never its to produce.
 */

const isAbort = (error: unknown) => error instanceof DOMException && error.name === 'AbortError'

const errorMessage = (error: unknown): string =>
  error instanceof ApiError ? error.message : 'Something went wrong. Please try again.'

/**
 * What the sale has already established, for the assistant to be told each turn.
 *
 * Built from the SAME state the UI renders, so the assistant's idea of the sale and the customer's cannot
 * diverge. Sent only when a model has actually been confirmed - before that there is nothing established
 * and the assistant should be searching, not being told what to assume.
 */
const saleContext = (sale: SaleState, language?: string): api.ChatContext | undefined =>
  // The language is reason enough on its own to send a context: a customer who spoke Hindi and then types
  // "haan" must still be answered in Hindi, and that can happen before any device is known.
  !sale.device && !language
    ? undefined
    : sale.device
    ? {
        language,
        deviceConfigId: sale.device.deviceConfigId,
        deviceName: sale.device.name,
        deviceVariantId: sale.answers.deviceVariantId,
        durationTimeId: sale.answers.durationTimeId,
        deviceConditionIds: sale.answers.conditionIds,
        // Sent only once the question has actually been put: an empty list means "nothing is wrong", and
        // before the question it would mean "not asked" - two different facts that must not look alike.
        functionalIssueIds: sale.step === 'faults' ? undefined : sale.answers.faultIds,
        accessoryIds: sale.answers.accessoryIds,
        accessoriesAssessed: sale.answers.accessoriesAssessed,
        pincode: sale.pincode,
        hasQuote: Boolean(sale.quote),
      }
    : { language }

/** A key that identifies one submission of one sale, reused across retries of that submission. */
const newIdempotencyKey = () =>
  `sellgpt-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`

export interface SaleEngine {
  sale: SaleState
  config: SellGptConfig | null
  categories: Category[]
  assessment: DeviceAssessment | null
  question: Question | null
  /** Ids selected but not yet confirmed, for a multiple-answer question. */
  pending: number[]
  /** Components picked but not yet confirmed, keyed by dimension (ram | processor | hardDrive). */
  pendingComponents: Record<string, number>
  busy: boolean
  /** True while the assistant is composing, so the UI can show it is thinking rather than stalled. */
  thinking: boolean
  /** False once a turn has come back reporting the assistant unreachable. */
  assistantAvailable: boolean
  /** A number or code typed into the chat while the sign-in form was open; the form picks it up. */
  prefill: { mobile?: string; otp?: string }
  reset: () => void
  send: (text: string) => Promise<void>
  choose: (key: string) => void
  confirmMulti: () => void
  /** Records the device's actual specification, or carries on unchanged. */
  setSpecification: (spec: { processorId?: number; ramId?: number; hardDriveId?: number; screenSizeId?: number }) => void
  chooseDevice: (device: ChosenDevice) => void
  createLead: (addressBookId: number, pickup?: { from: string; to: string }) => Promise<string | null>
  /** Remembers the language the customer was heard speaking, so replies keep coming back in it. */
  noteLanguage: (language: string) => void
  /**
   * True when the customer has asked to sign in on its own - from the sidebar rather than part-way
   * through a sale. The conversation shows the same mobile-and-code form either way.
   */
  /** The language the sale is being conducted in, or undefined for English. */
  language?: string
  signInRequested: boolean
  requestSignIn: () => void
  dismissSignIn: () => void
  clearError: () => void
}

/**
 * @param customerRegId The signed-in customer, when there is one. The engine uses it for ONE thing: to
 *   find out whether they already have a pickup address on file, so it does not ask a returning customer
 *   for a pincode it can read off the address they are about to choose anyway.
 */
export function useSaleEngine(customerRegId?: number): SaleEngine {
  const [sale, dispatch] = useReducer(saleReducer, initialSale)
  const [config, setConfig] = useState<SellGptConfig | null>(null)
  const [categories, setCategories] = useState<Category[]>([])
  const [assessment, setAssessment] = useState<DeviceAssessment | null>(null)
  const [pending, setPending] = useState<number[]>([])
  /** The components picked but not yet confirmed, keyed by dimension. Cleared when the step changes. */
  const [pendingComponents, setPendingComponents] = useState<Record<string, number>>({})
  const [busy, setBusy] = useState(false)
  const [thinking, setThinking] = useState(false)
  const [assistantDown, setAssistantDown] = useState(false)
  /**
   * A mobile number or code the customer typed into the CHAT while the sign-in form was open. Carried into
   * the form rather than discarded: typing it was the right instinct, and refusing it was the loop that
   * left a customer repeating their number at an assistant that is not allowed to take it.
   */
  const [prefill, setPrefill] = useState<{ mobile?: string; otp?: string }>({})
  /**
   * The language the microphone detected. Sticky on purpose: a customer who describes their phone in Tamil
   * and then answers "sari" should not be dropped back into English by a one-word reply that carries no
   * language of its own.
   */
  const [language, setLanguage] = useState<string | undefined>(undefined)
  /**
   * Signing in as its OWN action, not as step nine of a sale.
   *
   * A returning customer who just wants to see their sales had no way in: the form only appeared once a
   * device had been chosen, priced and confirmed. Their account, their history and their saved addresses
   * were all behind a sale they did not want to start.
   */
  const [signInRequested, setSignInRequested] = useState(false)
  /** The signed-in customer's saved pickup addresses, or null until they have been looked up. */
  const [savedAddresses, setSavedAddresses] = useState<api.Address[] | null>(null)

  /**
   * A saved address whose pincode is actually usable, or null.
   *
   * SIX digits, not merely present: saved addresses in this database include codes like 865 and 50032, and
   * adopting one would give the customer a pickup area that cannot be looked up - worse than just asking.
   */
  const savedPickup = useMemo(
    () => savedAddresses?.find((a) => /^\d{6}$/.test(String(a.pincode ?? ''))) ?? null,
    [savedAddresses],
  )
  const restored = useRef(false)

  const assistantAvailable = (config?.assistantAvailable ?? false) && !assistantDown

  // ── Load, restore, persist ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const controller = new AbortController()

    // A saved draft is restored before anything is fetched, so a refresh puts the customer back where
    // they were rather than at a fresh welcome screen while the network catches up.
    const draft = draftStorage.read<SaleState>()
    if (draft?.step && Array.isArray(draft.messages)) dispatch({ type: 'restore', state: draft })
    restored.current = true

    void (async () => {
      const [configResult, categoriesResult] = await Promise.allSettled([
        api.getConfig(controller.signal),
        api.getCategories(controller.signal),
      ])
      if (configResult.status === 'fulfilled') setConfig(configResult.value)
      // A config call that fails is not fatal: the assistant simply counts as unavailable, which is the
      // guided flow - the same outcome as OpenAI being down, and a working sale either way.
      else setConfig({ assistantAvailable: false, voiceInputAvailable: false, leadSource: 'sellgpt.in', otpExpiryMinutes: 5, otpResendDelaySeconds: 60, otpMaxAttempts: 5 })
      if (categoriesResult.status === 'fulfilled') setCategories(categoriesResult.value)
    })()

    return () => controller.abort()
  }, [])

  useEffect(() => {
    if (restored.current) draftStorage.write(sale)
  }, [sale])

  // A returning customer's saved addresses. Fetched as soon as they are known so the pincode question can
  // be skipped rather than asked and then immediately answered for them.
  useEffect(() => {
    if (!customerRegId) {
      setSavedAddresses(null)
      return
    }
    const controller = new AbortController()
    void (async () => {
      try {
        setSavedAddresses(await api.getAddresses(customerRegId, controller.signal))
      } catch (error) {
        // Not fatal: without this the customer is simply asked for a pincode, as before.
        if (!isAbort(error)) setSavedAddresses([])
      }
    })()
    return () => controller.abort()
  }, [customerRegId])



  /**
   * The other RAM/storage configurations of the chosen model group.
   *
   * For phones and tablets each configuration is a SEPARATE catalogue device, so search returns only one of
   * them and the rest are invisible. Until this was asked, someone selling an iPhone 15 256 GB was quoted
   * the 128 GB price, because that is the device the search happened to land on.
   */
  useEffect(() => {
    const device = sale.device
    if (!device?.modelGroupId || (device.configurationCount ?? 1) < 2) return
    if (sale.step !== 'configuration' || sale.configurations.length > 0) return

    const controller = new AbortController()
    void (async () => {
      try {
        const options = await api.getModelConfigurations(device.modelGroupId!, controller.signal)
        // One option is not a question. Two or more is the customer's to answer.
        if (options.length > 1) dispatch({ type: 'configurations', options })
      } catch (error) {
        // Not fatal: the sale continues on the configuration the search returned, which is what it did
        // before this question existed.
        if (!isAbort(error)) dispatch({ type: 'error', message: errorMessage(error) })
      }
    })()
    return () => controller.abort()
  }, [sale.device, sale.step, sale.configurations.length])

  // The assessment belongs to the chosen model and is re-fetched rather than stored: it carries prices
  // and question lists that an administrator can change between one visit and the next.
  useEffect(() => {
    const deviceConfigId = sale.device?.deviceConfigId
    if (!deviceConfigId) {
      setAssessment(null)
      return
    }
    if (assessment?.deviceConfigId === deviceConfigId) return

    const controller = new AbortController()
    void (async () => {
      try {
        setAssessment(await api.getAssessment(deviceConfigId, controller.signal))
      } catch (error) {
        if (!isAbort(error)) dispatch({ type: 'error', message: errorMessage(error) })
      }
    })()
    return () => controller.abort()
  }, [sale.device?.deviceConfigId, assessment?.deviceConfigId])

  const question = useMemo(
    () => questionFor(sale, categories, assessment, language),
    [sale, categories, assessment],
  )

  // Selections are per question, so moving on clears them rather than carrying an old answer forward.
  useEffect(() => {
    setPending([])
    setPendingComponents({})
  }, [sale.step, sale.device?.deviceConfigId])

  const say = useCallback((role: 'user' | 'assistant', text: string) => {
    if (text.trim()) dispatch({ type: 'say', role, text })
  }, [])

  /**
   * A signed-in customer with an address on file is NOT asked for a pincode.
   *
   * They gave Cashit that address already, they are about to pick it on the very next screen, and the
   * pincode is written on it - so asking "what is your six-digit pickup pincode?" makes a returning
   * customer type something the application is holding in its hand. The area is confirmed from their own
   * saved address instead, and they can still choose a different one at the confirmation step.
   */
  useEffect(() => {
    if (sale.step !== 'pincode' || sale.pincode) return
    // SIX digits, not merely present. Saved addresses in this database include codes like 865 and 50032,
    // The same usable-address test the "do not ask" guard uses, so the two can never disagree - one of
    // them silently adopting the address while the other still asked for a pincode is exactly the
    // contradiction a customer saw: "I'll use your saved address" followed by "what is your pincode?".
    const saved = savedPickup
    if (!saved?.pincode) return

    const code = String(saved.pincode)
    say('assistant', saved.cityName
      ? `I'll use your saved pickup address in ${saved.cityName}. You can change it before you confirm.`
      : "I'll use your saved pickup address. You can change it before you confirm.")
    void (async () => {
      try {
        dispatch({ type: 'pincode', pincode: code, pickup: await api.checkPickup(code) })
      } catch {
        // The address is already on file, so the sale carries on with it whatever the lookup says.
        dispatch({ type: 'pincode', pincode: code })
      }
    })()
  }, [sale.step, sale.pincode, savedPickup, say])

  /**
   * THE ASSISTANT ASKS THE QUESTION THE FLOW HAS MOVED TO.
   *
   * Without this the conversation is silent between answers: chips appear with nothing asking about
   * them, and the pincode step - which has no chips at all - looks like the sale has simply stopped. The
   * customer's last answer sits there, the composer quietly changes its placeholder, and nothing tells
   * them what to do. That is where the flow dead-ended.
   *
   * It speaks only when the last thing said was the CUSTOMER's - which is exactly the case after they
   * click a choice. When the assistant has just replied it has already asked its own question in its own
   * words, and repeating the canned prompt underneath would talk over it.
   */
  useEffect(() => {
    if (!question || thinking || busy) return
    if (sale.messages.at(-1)?.role !== 'user') return
    // The pincode is about to be taken from the customer's own saved address, so asking for it would
    // contradict the very next sentence ("I'll use your saved pickup address in ...").
    if (sale.step === 'pincode' && savedPickup) return
    say('assistant', question.prompt)
  }, [question, sale.messages, sale.step, savedPickup, thinking, busy, say])

  // ── The quote ─────────────────────────────────────────────────────────────────────────────────
  const fetchQuote = useCallback(
    async (state: SaleState) => {
      const body = quoteRequestFor(state, assessment ?? undefined)
      if (!body) return
      setBusy(true)
      try {
        const quote = await api.getPriceQuote(body)
        // The selection is stored with the quote, and the lead is created from it - not from the answers
        // held here. On this path they are the same object; on the assistant path they would not be.
        dispatch({ type: 'quote', quote, selection: body })
        if (quote.eligibility === 'Ineligible') {
          say('assistant', `Cashit cannot accept this device: ${quote.eligibilityReasons.join('; ')}`)
        } else if (quote.requiresManualValuation) {
          say(
            'assistant',
            'This one needs a person to look at it, so there is no automatic price. Continue and the Cashit team will value it and come back to you.',
          )
        } else {
          say(
            'assistant',
            fill(phrases(language).quote, { amount: new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(quote.sellerPayout) }),
          )
        }
      } catch (error) {
        if (isAbort(error)) return
        // The valuation service's own refusal - an unmapped processor, a device with no live price - is
        // repeated exactly. It is written for a customer, and softening it would hide the reason.
        dispatch({ type: 'error', message: errorMessage(error) })
        say('assistant', errorMessage(error))
        // Back to the last answer they gave, so a failed valuation can be retried by changing something
        // rather than leaving the sale sitting on a step whose only job is to fetch a price.
        dispatch({ type: 'step', step: 'accessories' })
      } finally {
        setBusy(false)
      }
    },
    [assessment, say],
  )

  useEffect(() => {
    if (sale.step === 'quote' && !sale.quote && !busy) void fetchQuote(sale)
  }, [sale, busy, fetchQuote])

  // ── Choosing ──────────────────────────────────────────────────────────────────────────────────
  const advanceFrom = useCallback(
    (state: SaleState) => {
      const next = nextAnswerableStep(state, assessment)
      if (next !== state.step) dispatch({ type: 'step', step: next })
    },
    [assessment],
  )

  const chooseDevice = useCallback(
    (device: ChosenDevice) => {
      say('user', device.name)
      dispatch({ type: 'device', device })
    },
    [say],
  )

  // The assessment arrives after the model is chosen, so the first answerable step can only be decided
  // once it is here. Until then the sale sits on 'configuration', which renders nothing.
  useEffect(() => {
    if (sale.step !== 'configuration' || !assessment) return
    if (assessment.deviceConfigId !== sale.device?.deviceConfigId) return
    // The group's configurations are still on their way. Advancing now would ask the age question, then
    // snap back to "which one do you have?" when they arrive - and worse, would let a sale slip past the
    // question entirely if the customer answered quickly.
    if ((sale.device?.configurationCount ?? 1) > 1 && sale.configurations.length === 0) return
    if (questionFor(sale, categories, assessment, language)) return
    const step = firstAnswerableStep(sale, assessment)
    if (step !== sale.step) dispatch({ type: 'step', step })
  }, [sale, assessment, categories])

  /** The catalogue-only path: what the customer typed, matched against Cashit's own search. */
  const searchCatalogue = useCallback(
    /**
     * @param alreadyEchoed True when the customer's choice is ALREADY in the conversation - picking a
     *   "do you mean ...?" suggestion says it before searching, and a second echo reads as a stutter.
     *   Passed explicitly rather than inferred from the last message: this runs after an await, so any
     *   closure over the conversation is a render behind by the time it matters.
     */
    async (text: string, alreadyEchoed = false) => {
      try {
        const results = await api.searchDevices(text, sale.categoryId)
        const devices: ChosenDevice[] = results
          .filter((r) => r.resultType === 'device')
          .map((r) => ({
            deviceConfigId: r.id,
            name: r.name,
            brand: r.brand,
            category: r.category,
            modelGroupId: r.modelGroupId,
            configurationCount: r.configurationCount,
          }))

        if (devices.length === 0) {
          // One mistyped letter is not "we do not buy that device". Offer the closest catalogue names and
          // let the customer confirm which they meant.
          const suggestions = await api.getSuggestions(text, sale.categoryId).catch(() => [])
          if (suggestions.length > 0) {
            dispatch({ type: 'suggestions', names: suggestions })
            say('assistant', suggestions.length === 1
              ? `Do you mean ${suggestions[0]}?`
              : 'I could not find that exactly. Do you mean one of these?')
            return
          }
          say('assistant', "I could not find that in Cashit's catalogue. Try the brand and model, for example 'Samsung Galaxy S23'.")
          return
        }
        if (devices.length === 1) {
          if (alreadyEchoed) dispatch({ type: 'device', device: devices[0] })
          else chooseDevice(devices[0])
          say('assistant', `${devices[0].name} it is. A few quick questions and I'll get you a price.`)
          return
        }
        dispatch({ type: 'candidates', devices })
        say('assistant', 'I found a few that match. Which one is yours?')
      } catch (error) {
        if (!isAbort(error)) say('assistant', errorMessage(error))
      }
    },
    [sale.categoryId, say, chooseDevice],
  )

  const choose = useCallback(
    (key: string) => {
      const parts = key.split(':')
      const kind = parts[0]

      // component:<dimension>:<id> - picked now, committed by the confirm button, so the question stays
      // optional and a half-made correction never reaches a price.
      if (kind === 'component') {
        const dimension = parts[1]
        const componentId = Number(parts[2])
        if (!Number.isFinite(componentId)) return
        setPendingComponents((current) =>
          current[dimension] === componentId
            ? Object.fromEntries(Object.entries(current).filter(([k]) => k !== dimension))
            : { ...current, [dimension]: componentId },
        )
        return
      }

      const id = Number(parts[1])
      if (!Number.isFinite(id)) return

      switch (kind) {
        case 'category': {
          const category = categories.find((c) => c.id === id)
          if (!category) return
          say('user', category.categoryName)
          dispatch({ type: 'category', id, name: category.categoryName })
          say('assistant', `Good. Which ${category.categoryName.toLowerCase()} is it? Type the brand and model.`)
          return
        }
        case 'device': {
          const device = sale.candidates.find((d) => d.deviceConfigId === id)
          if (device) chooseDevice(device)
          return
        }
        case 'suggestion': {
          const name = sale.suggestions[id]
          if (!name) return
          say('user', name)
          // Searched again rather than trusted: the suggestion is a catalogue NAME, and the sale still
          // proceeds from whatever the real ranking returns for it.
          setBusy(true)
          void searchCatalogue(name, true).finally(() => setBusy(false))
          return
        }
        case 'groupconfig': {
          // A different catalogue device, with its own price - so it is chosen, not merely noted.
          const chosen = sale.configurations.find((c) => c.deviceId === id)
          if (!chosen) return
          say('user', chosen.name)
          dispatch({ type: 'device', device: { deviceConfigId: chosen.deviceId, name: chosen.name } })
          return
        }
        case 'configuration':
          say('user', assessment?.modelVariants.find((v) => v.deviceVariantId === id)?.name ?? 'That configuration')
          dispatch({ type: 'configuration', deviceVariantId: id })
          return
        case 'age':
          say('user', assessment?.durationTimes.find((d) => d.id === id)?.durationName ?? 'That age')
          dispatch({ type: 'age', durationTimeId: id })
          return
        // The three multiple-answer questions only toggle here; nothing moves until confirmMulti.
        case 'condition':
        case 'fault':
        case 'accessory':
          setPending((current) =>
            current.includes(id) ? current.filter((x) => x !== id) : [...current, id],
          )
          return
        default:
          return
      }
    },
    // The WHOLE sale, not a few cherry-picked fields. Listing sale.candidates alone meant this callback
    // kept a stale sale whenever some OTHER part of it changed: picking a "do you mean...?" suggestion did
    // nothing at all, because sale.suggestions was still the empty array captured before they arrived. The
    // same trap was one dispatch away from sale.configurations. Re-memoising on every sale change costs a
    // closure; getting it wrong costs a button that silently does nothing.
    [categories, sale, assessment, say, chooseDevice, searchCatalogue],
  )

  const confirmMulti = useCallback(() => {
    if (sale.step === 'components') {
      const labels = (question?.groups ?? [])
        .flatMap((g) => g.choices)
        .filter((c) => Object.values(pendingComponents).includes(Number(c.key.split(':')[2])))
        .map((c) => c.label)
      say('user', labels.length ? labels.join(', ') : 'It is as listed')
      dispatch({
        type: 'components',
        ramId: pendingComponents.ram,
        processorId: pendingComponents.processor,
        hardDriveId: pendingComponents.hardDrive,
      })
      setPendingComponents({})
      return
    }

    const chosen = [...pending]
    const labels = (question?.choices ?? [])
      .filter((c) => chosen.includes(Number(c.key.split(':')[1])))
      .map((c) => c.label)

    switch (sale.step) {
      case 'condition':
        say('user', labels.length ? labels.join(', ') : 'Nothing to report')
        dispatch({ type: 'conditions', ids: chosen })
        break
      case 'faults':
        say('user', labels.length ? labels.join(', ') : 'Everything works')
        dispatch({ type: 'faults', ids: chosen })
        break
      case 'accessories':
        say('user', labels.length ? labels.join(', ') : 'I have none of these')
        dispatch({ type: 'accessories', ids: chosen })
        break
      default:
        return
    }
    setPending([])
  }, [pending, pendingComponents, question, sale.step, say])

  // Skipping a question the catalogue has no options for has to happen after the answer is stored, so it
  // is driven off the state rather than done inline in each branch above.
  useEffect(() => {
    if (!assessment) return
    if (sale.step === 'quote' || sale.step === 'pincode') return
    if (sale.step === 'configuration'
        && (sale.device?.configurationCount ?? 1) > 1 && sale.configurations.length === 0) return
    if (questionFor(sale, categories, assessment, language)) return
    if (['configuration', 'components', 'age', 'condition', 'faults', 'accessories'].includes(sale.step)) {
      advanceFrom(sale)
    }
  }, [sale, assessment, categories, advanceFrom])

  // ── Typing ────────────────────────────────────────────────────────────────────────────────────



  const enterPincode = useCallback(
    async (text: string) => {
      const pincode = text.replace(/\D/g, '')
      if (pincode.length !== 6) {
        say('assistant', 'An Indian pincode is exactly six digits. Could you check it?')
        return
      }
      try {
        const pickup = await api.checkPickup(pincode)
        dispatch({ type: 'pincode', pincode, pickup })
        say(
          'assistant',
          pickup.serviceable
            ? `Pickup is available around ${pickup.city}. Let me get your price.`
            : 'That pincode is not in our serviceable list yet, so I cannot promise a pickup there. Here is your price anyway.',
        )
      } catch (error) {
        if (isAbort(error)) return
        // A lookup that fails must not block the sale: the pincode is recorded, the serviceability
        // question stays unanswered, and nothing is claimed about it either way.
        dispatch({ type: 'pincode', pincode })
        say('assistant', 'I could not check that area just now. Let me get your price.')
      }
    },
    [say],
  )

  const send = useCallback(
    async (raw: string) => {
      const text = raw.trim()
      if (!text || busy || thinking) return

      say('user', text)
      // Learned from what they WRITE, not only from what they say into the microphone: the guided
      // questions that follow a tapped choice are asked in this language.
      noteTypedLanguage(text)

      // The pincode is read here whatever the path: it is the one answer that is typed and must be
      // validated as six digits before the sale can move on.
      if (sale.step === 'pincode') {
        setBusy(true)
        try {
          await enterPincode(text)
        } finally {
          setBusy(false)
        }
        return
      }

      // ONCE THE SALE IS AT THE FORM, THE ASSISTANT IS NOT THE RIGHT ANSWERER.
      //
      // Identity and the pickup details are collected by a form, and the assistant is forbidden - rightly -
      // from touching an OTP. Sending these turns to it produced a loop a customer could not escape: they
      // typed their mobile number into the chat and were told "please verify it in the application", over
      // and over, while the form sat there unnoticed. Handled locally instead, and a number typed into the
      // chat is carried INTO the form rather than being rejected, because typing it was the right instinct.
      if (sale.step === 'identify' || sale.step === 'confirm') {
        const digits = text.replace(/\D/g, '')

        if (/^[6-9]\d{9}$/.test(digits)) {
          setPrefill({ mobile: digits })
          say('assistant', "I've put that number into the form just below — press “Send code” and I'll text you a one-time code.")
        } else if (digits.length === 6) {
          // SIX digits is a pincode, not a code: Cashit's OTP is four digits (OtpGenerator). Reading it as
          // a code silently swallowed a customer correcting their pickup area at the last moment - the
          // sale kept the pincode they had already replaced.
          setBusy(true)
          try {
            await enterPincode(digits)
          } finally {
            setBusy(false)
          }
        } else if (digits.length >= 4 && digits.length <= 5) {
          setPrefill({ otp: digits })
          say('assistant', "I've entered that code into the form below for you.")
        } else {
          say('assistant', 'Just below this message there is a short form to confirm your number and your pickup details — fill that in and your sale request is done.')
        }
        return
      }

      if (!assistantAvailable) {
        setBusy(true)
        try {
          if (sale.step === 'category' || sale.step === 'model') await searchCatalogue(text)
          else say('assistant', 'Please pick one of the options above so I keep your answers accurate.')
        } finally {
          setBusy(false)
        }
        return
      }

      setThinking(true)
      try {
        const history = sale.messages.map((m) => ({ role: m.role, content: m.text }))
        const reply = await api.chat(text, history, saleContext(sale, language))

        if (!reply.assistantAvailable) {
          // One failed turn switches the whole session to the guided flow rather than retrying the
          // assistant on every message: a customer should not pay a timeout's wait more than once.
          setAssistantDown(true)
          say('assistant', reply.reply)
          if (sale.step === 'category' || sale.step === 'model') await searchCatalogue(text)
          return
        }

        applyToolResults(reply.tools)
        say('assistant', reply.reply)
      } catch (error) {
        if (isAbort(error)) return
        setAssistantDown(true)
        say('assistant', "I'm having trouble with the assistant. Let's carry on step by step.")
        if (sale.step === 'category' || sale.step === 'model') await searchCatalogue(text)
      } finally {
        setThinking(false)
      }
    },
    [busy, thinking, sale, assistantAvailable, say, enterPincode, searchCatalogue],
  )

  /**
   * Reads the server's tool results into the sale. This is how the assistant moves the sale forward: it
   * never sets a field directly, and anything it did not actually look up cannot appear here.
   */
  function applyToolResults(tools: api.ChatToolResult[]): void {
    for (const tool of tools) {
      const result = tool.result as Record<string, unknown> | null
      if (!result || typeof result !== 'object') continue

      if (tool.tool === 'find_device' && Array.isArray(result.matches)) {
        const devices = (result.matches as Array<Record<string, unknown>>).map((m) => ({
          deviceConfigId: Number(m.deviceConfigId),
          name: String(m.name ?? ''),
          brand: m.brand ? String(m.brand) : undefined,
          category: m.category ? String(m.category) : undefined,
          modelGroupId: m.modelGroupId ? Number(m.modelGroupId) : undefined,
          configurationCount: m.configurationCount ? Number(m.configurationCount) : undefined,
        }))
        // One match is still the customer's to confirm. The assistant's words may already read as though
        // it has been settled, but the sale does not move until they say so - a wrong model chosen for
        // them is a wrong price and, eventually, a rejected pickup.
        if (devices.length > 0) dispatch({ type: 'candidates', devices })
      }

      if (tool.tool === 'request_valuation' && result.priced === true) {
        // The assistant asked Cashit for a price and Cashit answered. The panel shows THIS - the
        // valuation service's own figure, passed through the server - not the number in the reply text,
        // and not a second quote fetched from whatever this browser happens to think was answered.
        const selection = result.selection as PricedSelection | undefined
        if (!selection?.deviceConfigId) continue

        // BEFORE the quote, never after: the 'device' action deliberately clears any quote, because a
        // price for one device must not survive a change of device. Dispatching it second would throw
        // away the price that was just established.
        if (!sale.device || sale.device.deviceConfigId !== selection.deviceConfigId) {
          dispatch({
            type: 'device',
            device: { deviceConfigId: selection.deviceConfigId, name: String(result.deviceName ?? '') },
          })
        }

        dispatch({
          type: 'quote',
          selection,
          quote: {
            deviceConfigId: selection.deviceConfigId,
            deviceName: String(result.deviceName ?? ''),
            deviceVariantId: selection.deviceVariantId,
            variantName: typeof result.configuration === 'string' ? result.configuration : undefined,
            finalPrice: Number(result.offer ?? 0),
            sellerPayout: Number(result.offer ?? 0),
            couponBonus: Number(result.couponBonus ?? 0),
            couponCode: typeof result.couponCode === 'string' ? result.couponCode : undefined,
            couponRejectedReason:
              typeof result.couponRejectedReason === 'string' ? result.couponRejectedReason : undefined,
            eligibility: (result.eligibility as PriceQuote['eligibility']) ?? 'Eligible',
            eligibilityReasons: Array.isArray(result.eligibilityReasons)
              ? (result.eligibilityReasons as string[])
              : [],
            requiresManualValuation: result.requiresManualValuation === true,
            condition: '',
          },
        })
      }

      if (tool.tool === 'check_pickup' && typeof result.pincode === 'string') {
        dispatch({
          type: 'pincode',
          pincode: result.pincode,
          pickup: {
            pincode: result.pincode,
            serviceable: result.serviceable === true,
            area: typeof result.area === 'string' ? result.area : undefined,
            areas: Array.isArray(result.areas) ? (result.areas as string[]) : [],
            city: typeof result.city === 'string' ? result.city : undefined,
            state: typeof result.state === 'string' ? result.state : undefined,
          },
        })
      }
    }
  }

  // ── The lead ──────────────────────────────────────────────────────────────────────────────────
  const createLead = useCallback(
    async (addressBookId: number, pickup?: { from: string; to: string }): Promise<string | null> => {
      // The selection the QUOTE was priced from, never the answers this client believes were given. They
      // are the same thing on the guided path and are not on the assistant path, and the difference is
      // the customer being shown one figure and selling at another.
      const body = sale.pricedSelection
      if (!body || !sale.quote) return null

      // Generated once and reused, so a double tap or a retry after a dropped connection is ONE lead.
      const key = sale.idempotencyKey ?? newIdempotencyKey()
      if (!sale.idempotencyKey) dispatch({ type: 'idempotencyKey', key })

      setBusy(true)
      try {
        const created = await api.createLead(
          {
            ...body,
            addressBookId,
            leadSource: config?.leadSource ?? 'sellgpt.in',
            loginDevice: 'web',
            // Without these the lead reaches the admin with "Pickup From ---", and somebody has to ring
            // the customer to ask what they would have told us here.
            pickupFrom: pickup?.from,
            pickupToDate: pickup?.to,
          },
          key,
        )
        dispatch({ type: 'lead', leadNumber: created.leadNumber })
        say('assistant', fill(phrases(language).doneMessage, { leadNumber: created.leadNumber }))
        return created.leadNumber
      } catch (error) {
        if (isAbort(error)) return null
        dispatch({ type: 'error', message: errorMessage(error) })
        return null
      } finally {
        setBusy(false)
      }
    },
    [sale, assessment, config, say],
  )

  const setSpecification = useCallback(
    (spec: { processorId?: number; ramId?: number; hardDriveId?: number; screenSizeId?: number }) => {
      const changed = Object.values(spec).filter(Boolean).length
      say('user', changed > 0 ? 'That is my specification' : 'The specification is correct')
      dispatch({ type: 'components', ...spec })
    },
    [say],
  )

  const reset = useCallback(() => {
    draftStorage.clear()
    setAssessment(null)
    setPending([])
    setPrefill({})
    dispatch({ type: 'reset' })
  }, [])

  /**
   * English is not recorded. Every device name, option label and price already comes back in English, so
   * marking a sale "english" would add a line to the assistants instructions that changes nothing - and
   * the absence of a language is what lets the model simply follow whatever the customer writes.
   */
  /**
   * The language of a message the customer typed, from its script.
   *
   * Voice already told us; typing did not. A customer who writes in Devanagari and then taps a choice was
   * asked the next question in English, because the guided flow had no idea what language the sale was
   * being conducted in - which is exactly what happened after "मैं अपना iPhone 13 बेचना चाहता हूँ".
   */
  const noteTypedLanguage = useCallback((text: string) => {
    const detected = languageOfScript(text)
    if (detected) setLanguage(detected)
  }, [])

  const noteLanguage = useCallback((detected: string) => {
    const clean = detected.trim().toLowerCase()
    if (!clean || clean === "english") return
    setLanguage(clean)
  }, [])

  return {
    sale,
    config,
    categories,
    assessment,
    question,
    pending,
    pendingComponents,
    busy,
    thinking,
    assistantAvailable,
    prefill,
    reset,
    send,
    choose,
    confirmMulti,
    setSpecification,
    chooseDevice,
    createLead,
    noteLanguage,
    language,
    signInRequested,
    requestSignIn: useCallback(() => setSignInRequested(true), []),
    dismissSignIn: useCallback(() => setSignInRequested(false), []),
    clearError: () => dispatch({ type: 'error', message: undefined }),
  }
}
